import { format } from 'url';

import React, { Component, Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import VariableInputDrawer from '@confluence-classic/confluence-fabric-editor/src/components/TemplateVariableInput/VariableInputDrawer.tsx';

import { usePageSpaceKey } from '@confluence/page-context';
import { QueryRenderer } from '@confluence/route-manager/entry-points/QueryRenderer.ts';
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { CreateDialogContext } from '@confluence/create-dialog-context';

import { PseudoCreateDialog } from '../PseudoCreateDialog/index.js';
import { LegacyCreateDialogCssOverridesLoader } from '../../tech-debt/LegacyCssOverrides/index.js';

import { mapV1UrltoV2, queryMatcher } from './helpers.js';

class CreateDialogQueryRendererComponent extends Component {
	static propTypes = {
		getQueryParams: PropTypes.func,
		setQueryParams: PropTypes.func,
		replace: PropTypes.func,
		spaceKey: PropTypes.string,
		showVariableInputDrawer: PropTypes.bool,
	};

	static getDerivedStateFromProps(props) {
		const { getQueryParams, showVariableInputDrawer } = props;

		const {
			createDialog,
			createDialogSpaceKey,
			createDialogBlueprintId,
			createDialogTemplateId,
			parentPageId,
		} = getQueryParams();

		return {
			isCreateDialogOpen: createDialog === 'true',
			shouldSuppressDialog: !!(
				createDialogSpaceKey &&
				(createDialogBlueprintId || createDialogTemplateId)
			),
			parentPageId,
			showVariableInputDrawer,
		};
	}

	state = {
		isCreateDialogOpen: false,
		shouldSuppressDialog: false,
		showVariableInputDrawer: false,
	};

	componentWillUnmount() {
		const { getQueryParams, spaceKey, replace } = this.props;
		if (this.state.isCreateDialogOpen) {
			const { ccProxyTarget } = getQueryParams();
			if (ccProxyTarget) {
				const parsed = mapV1UrltoV2(window.location.pathname, getQueryParams(), spaceKey);
				delete parsed.query.createDialog;
				delete parsed.query.ccProxyTarget;

				const newPath = format({
					origin: window.location.origin,
					pathname: parsed.url,
					query: parsed.query,
				});

				replace(newPath);
			}
		}
	}

	closeDialog = () => {
		const { setQueryParams } = this.props;

		setQueryParams({ createDialog: null, parentPageId: null });
	};

	render() {
		const { isCreateDialogOpen, parentPageId, shouldSuppressDialog, showVariableInputDrawer } =
			this.state;

		return isCreateDialogOpen && !showVariableInputDrawer ? (
			<PseudoCreateDialog
				key="create"
				open={isCreateDialogOpen}
				onClose={this.closeDialog}
				shouldSuppressDialog={shouldSuppressDialog}
				parentPageId={parentPageId}
			/>
		) : null;
	}
}

export const CreateDialogQueryRenderer = () => {
	const { getQueryParams, setQueryParams, replace } = useContext(RoutesContext);
	const [spaceKey] = usePageSpaceKey();

	const { showTemplateVariableInputDrawer } = useContext(CreateDialogContext);

	return (
		<Fragment>
			<QueryRenderer queryMatcher={queryMatcher}>
				<ErrorBoundary
					attribution={Attribution.PAGE_MODES}
					modal={{
						onClose: () =>
							setQueryParams({
								createDialog: null,
								parentPageId: null,
							}),
					}}
				>
					<CreateDialogQueryRendererComponent
						getQueryParams={getQueryParams}
						setQueryParams={setQueryParams}
						replace={replace}
						spaceKey={spaceKey}
						showVariableInputDrawer={showTemplateVariableInputDrawer}
					/>
					<LegacyCreateDialogCssOverridesLoader />
				</ErrorBoundary>
			</QueryRenderer>
			<VariableInputDrawer />
		</Fragment>
	);
};
